import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSessionState } from "@/hooks/useSessionState";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FileImage,
  FileAudio,
  Upload,
  Trash2,
  Search,
  RefreshCw,
  Edit,
  X,
  Eye,
  Square,
  CheckSquare,
  LayoutGrid,
  List,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { usePagination } from "@/hooks/usePagination";

type MediaItem = {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
  uploadedAt: string;
  owner: string;
  path: string;
  uploaderRoleType?: "user" | "admin" | null;
};

const MediaLibrary = () => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [activeTab, setActiveTab] = useSessionState("mediaLibrary.activeTab", "all");
  const [searchQuery, setSearchQuery] = useSessionState("mediaLibrary.searchQuery", "");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<MediaItem | null>(null);
  const [currentRenameItem, setCurrentRenameItem] = useState<MediaItem | null>(
    null
  );
  const [newFilename, setNewFilename] = useState("");
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
  const [currentPage, setCurrentPage] = useSessionState("mediaLibrary.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("mediaLibrary.pageSize", 15);
  const [totalItems, setTotalItems] = useState(0);
  const [viewStyle, setViewStyle] = useSessionState<"grid" | "list">("mediaLibrary.viewStyle", "grid");
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    completed: number;
    isUploading: boolean;
  }>({ total: 0, completed: 0, isUploading: false });
  const [sortOrder, setSortOrder] = useSessionState<
    "newest" | "oldest" | "name" | "size-asc" | "size-desc"
  >("mediaLibrary.sortOrder", "newest");
  const [uploadedByFilter, setUploadedByFilter] = useSessionState<
    "all" | "users" | "admins"
  >("mediaLibrary.uploadedByFilter", "admins");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Add file size limits
  const MAX_AUDIO_SIZE = 50 * 1024 * 1024; // 50MB in bytes
  const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1MB in bytes

  // Add supported MIME types for audio files
  const SUPPORTED_AUDIO_TYPES = [
    "audio/mpeg",
    "audio/mp3",
    "audio/wav",
    "audio/ogg",
    "audio/x-m4a",
    "audio/aac",
  ];

  // Format date helper function
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch media items from Supabase storage with simpler, more direct approach
  const fetchMediaItems = async () => {
    // Only show loading if we don't have any data yet
    const shouldShowLoading = mediaItems.length === 0;
    if (shouldShowLoading) {
      setIsLoading(true);
    }
    try {
      console.log("Fetching media items from storage...");

      // Get all files from the root level first
      const { data: rootLevelData, error: rootLevelError } =
        await supabase.storage.from("media").list("", { limit: 1000 });

      if (rootLevelError) {
        console.error("Error fetching root level:", rootLevelError);
        throw rootLevelError;
      }

      console.log("Root level items:", rootLevelData?.length || 0);

      // Initialize array to store all media items with const instead of let
      const allMediaItems: MediaItem[] = [];

      // Process each item at the root level
      if (rootLevelData) {
        for (const item of rootLevelData) {
          // Check if this is a "folder"
          if (item && !item.metadata) {
            console.log(`Found folder: ${item.name}`);
            // This is a folder, get its contents
            const { data: folderContents, error: folderError } =
              await supabase.storage
                .from("media")
                .list(item.name, { limit: 1000 });

            if (folderError) {
              console.error(`Error listing folder ${item.name}:`, folderError);
              continue;
            }

            console.log(
              `Items in folder ${item.name}:`,
              folderContents?.length || 0
            );

            // Process each file in the folder
            if (folderContents) {
              for (const file of folderContents) {
                if (file && file.metadata) {
                  // If it has metadata, it's a file not a subfolder
                  // Get the public URL for this file
                  const { data: urlData } = supabase.storage
                    .from("media")
                    .getPublicUrl(`${item.name}/${file.name}`);

                  allMediaItems.push({
                    id: file.id,
                    name: file.name,
                    type: file.metadata.mimetype || "",
                    url: urlData.publicUrl,
                    size: file.metadata.size || 0,
                    uploadedAt: file.created_at || "",
                    owner: item.name,
                    path: `${item.name}/${file.name}`,
                  });
                }
              }
            }
          } else if (item && item.metadata) {
            // This is a file at root level
            console.log(`Found root file: ${item.name}`);
            const { data: urlData } = supabase.storage
              .from("media")
              .getPublicUrl(item.name);

            allMediaItems.push({
              id: item.id,
              name: item.name,
              type: item.metadata.mimetype || "",
              url: urlData.publicUrl,
              size: item.metadata.size || 0,
              uploadedAt: item.created_at || "",
              owner: "root",
              path: item.name,
            });
          }
        }
      }

      // After processing all media items, sort them by upload date (most recent first)
      console.log(`Total media items found: ${allMediaItems.length}`);

      // Sort media items by upload date (most recent first)
      allMediaItems.sort((a, b) => {
        // Convert strings to Date objects
        const dateA = new Date(a.uploadedAt);
        const dateB = new Date(b.uploadedAt);

        // Sort in descending order (newest first)
        return dateB.getTime() - dateA.getTime();
      });

      // Fetch user role information to determine uploader type
      try {
        // Get unique owner UIDs (excluding "root")
        const uniqueOwnerUids = [
          ...new Set(
            allMediaItems
              .map((item) => item.owner)
              .filter((owner) => owner !== "root")
          ),
        ];

        if (uniqueOwnerUids.length > 0) {
          // Query profiles to get profile IDs for each owner UID
          const { data: profiles, error: profilesError } = await supabase
            .from("profiles")
            .select("id, uid")
            .in("uid", uniqueOwnerUids);

          if (profilesError) {
            console.error("Error fetching profiles:", profilesError);
          } else if (profiles && profiles.length > 0) {
            // Create a map of UID to profile ID
            const uidToProfileIdMap = new Map(
              profiles.map((p) => [p.uid, p.id])
            );

            // Get all profile IDs
            const profileIds = profiles.map((p) => p.id);

            // Query user_to_roles to get roles for each profile
            const { data: userRoles, error: userRolesError } = await supabase
              .from("user_to_roles")
              .select("user_id, role_id")
              .in("user_id", profileIds);

            if (userRolesError) {
              console.error("Error fetching user roles:", userRolesError);
            } else if (userRoles) {
              // Group roles by user_id
              const userRolesMap = new Map<number, number[]>();
              userRoles.forEach((ur) => {
                const existing = userRolesMap.get(ur.user_id) || [];
                existing.push(ur.role_id);
                userRolesMap.set(ur.user_id, existing);
              });

              // Create a map of UID to role type
              const uidToRoleTypeMap = new Map<string, "user" | "admin">();
              uidToProfileIdMap.forEach((profileId, uid) => {
                const roleIds = userRolesMap.get(profileId) || [];
                // If user has ONLY role 5, they're a "user", otherwise "admin"
                const isUserOnly =
                  roleIds.length === 1 && roleIds[0] === 5;
                uidToRoleTypeMap.set(uid, isUserOnly ? "user" : "admin");
              });

              // Map role type back to media items
              allMediaItems.forEach((item) => {
                if (item.owner !== "root") {
                  item.uploaderRoleType =
                    uidToRoleTypeMap.get(item.owner) || null;
                } else {
                  item.uploaderRoleType = null;
                }
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user role information:", error);
        // Continue even if role fetching fails - items will just have null uploaderRoleType
      }

      setMediaItems(allMediaItems);
      setTotalItems(allMediaItems.length);
      setHasInitialized(true);
    } catch (error) {
      console.error("Error fetching media items:", error);
      toast({
        title: "Error",
        description: "Failed to load media items",
        variant: "destructive",
      });
    } finally {
      if (shouldShowLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (user && !hasInitialized) {
      fetchMediaItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Filter media items based on active tab, search query, folder, and uploadedBy
  const filteredMediaItems = useMemo(() => {
    return mediaItems
      .filter((item) => {
        const matchesType =
          activeTab === "all" ||
          (activeTab === "images" && item.type.startsWith("image/")) ||
          (activeTab === "audio" && item.type.startsWith("audio/"));

        const matchesSearch = item.name
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        const matchesFolder = true; // Always show all folders

        const matchesUploadedBy =
          uploadedByFilter === "all" ||
          (uploadedByFilter === "users" &&
            item.uploaderRoleType === "user") ||
          (uploadedByFilter === "admins" &&
            item.uploaderRoleType === "admin");

        return matchesType && matchesSearch && matchesFolder && matchesUploadedBy;
      })
      .sort((a, b) => {
        if (sortOrder === "newest") {
          const dateA = new Date(a.uploadedAt);
          const dateB = new Date(b.uploadedAt);
          return dateB.getTime() - dateA.getTime();
        } else if (sortOrder === "oldest") {
          const dateA = new Date(a.uploadedAt);
          const dateB = new Date(b.uploadedAt);
          return dateA.getTime() - dateB.getTime();
        } else if (sortOrder === "name") {
          // Sort by name
          return a.name.localeCompare(b.name);
        } else if (sortOrder === "size-asc") {
          // Sort by size ascending (smallest first)
          return a.size - b.size;
        } else if (sortOrder === "size-desc") {
          // Sort by size descending (largest first)
          return b.size - a.size;
        } else {
          // Default to newest
          const dateA = new Date(a.uploadedAt);
          const dateB = new Date(b.uploadedAt);
          return dateB.getTime() - dateA.getTime();
        }
      });
  }, [mediaItems, activeTab, searchQuery, sortOrder, uploadedByFilter]);

  // Get unique folder names for the dropdown
  const folders = [...new Set(mediaItems.map((item) => item.owner))].sort();

  // Calculate pagination using usePagination hook
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: filteredMediaItems.length,
    itemsPerPage: pageSize,
    currentPage,
  });

  // Get paginated items
  const paginatedItems = filteredMediaItems.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Handle page navigation
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + " MB";
    else return (bytes / 1073741824).toFixed(1) + " GB";
  };

  const handleItemClick = (id: string, event?: React.MouseEvent) => {
    // If the click was directly on the checkbox, don't toggle preview
    if (event && (event.target as HTMLElement).closest(".checkbox-area")) {
      return;
    }

    // Find the item
    const item = mediaItems.find((item) => item.id === id);
    if (item) {
      setPreviewItem(item);
      setIsPreviewDialogOpen(true);
    }
  };

  const handleCheckboxToggle = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((itemId) => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredMediaItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredMediaItems.map((item) => item.id));
    }
  };

  // Add a refreshMediaItems function that forces a complete reload
  const refreshMediaItems = () => {
    // Clear current items first
    setMediaItems([]);
    setIsLoading(true);
    setHasInitialized(false);

    // Small delay to ensure UI updates
    setTimeout(() => {
      fetchMediaItems();
    }, 100);
  };

  // Generate a safe filename to avoid collisions while preserving the original name
  const generateSafeFilename = (filename: string): string => {
    // Extract the file extension
    const lastDotIndex = filename.lastIndexOf(".");
    const ext = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : "";
    const nameWithoutExt =
      lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;

    // Add a timestamp to ensure uniqueness while preserving the original name
    const timestamp = new Date().getTime();
    return `${nameWithoutExt}_${timestamp}${ext}`;
  };

  const handleFileUpload = async () => {
    if (!uploadFiles || !user) return;

    // Reset and initialize upload progress
    setUploadProgress({
      total: uploadFiles.length,
      completed: 0,
      isUploading: true,
    });

    try {
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];

        // Check file size based on type
        const isAudioFile = file.type.startsWith("audio/");
        const maxSize = isAudioFile ? MAX_AUDIO_SIZE : MAX_IMAGE_SIZE;

        if (file.size > maxSize) {
          throw new Error(
            `File "${
              file.name
            }" exceeds the maximum size limit of ${formatFileSize(maxSize)}`
          );
        }

        // Validate audio file types
        if (isAudioFile && !SUPPORTED_AUDIO_TYPES.includes(file.type)) {
          throw new Error(
            `File "${file.name}" is not a supported audio format. Please use MP3, WAV, OGG, M4A, or AAC.`
          );
        }

        // Generate a safe filename that preserves the original name
        const safeFileName = generateSafeFilename(file.name);
        const filePath = `${user.uid}/${safeFileName}`;

        const { error: uploadError } = await supabase.storage
          .from("media")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
            contentType: file.type, // Explicitly set the content type
          });

        if (uploadError) throw uploadError;

        // Update progress after each file is uploaded
        setUploadProgress((prev) => ({
          ...prev,
          completed: prev.completed + 1,
        }));
      }

      toast({
        title: "Upload Successful",
        description: `${uploadFiles.length} files uploaded to the media library`,
      });

      // Refresh the media items after upload
      setHasInitialized(false);
      fetchMediaItems();
      setIsUploadDialogOpen(false);
      setUploadFiles(null);
    } catch (error) {
      console.error("Error uploading files:", error);
      toast({
        title: "Upload Error",
        description:
          error instanceof Error ? error.message : "Failed to upload files",
        variant: "destructive",
      });
    } finally {
      setUploadProgress({ total: 0, completed: 0, isUploading: false });
    }
  };

  // Function to handle renaming a file
  const handleRenameFile = async () => {
    if (!currentRenameItem || !user || !newFilename.trim()) return;

    setIsLoading(true);
    try {
      // Get the file extension from the current name
      const fileExt = currentRenameItem.name.split(".").pop() || "";

      // Ensure the new filename has the same extension
      let finalNewFilename = newFilename.trim();
      if (!finalNewFilename.endsWith(`.${fileExt}`)) {
        finalNewFilename = `${finalNewFilename}.${fileExt}`;
      }

      // Copy file to new location (with new name)
      const { data, error: copyError } = await supabase.storage
        .from("media")
        .copy(currentRenameItem.path, `${user.uid}/${finalNewFilename}`);

      if (copyError) throw copyError;

      // Once copy succeeds, remove the old file
      const { error: removeError } = await supabase.storage
        .from("media")
        .remove([currentRenameItem.path]);

      if (removeError) throw removeError;

      toast({
        title: "File Renamed",
        description: `File renamed successfully to ${finalNewFilename}`,
      });

      // Refresh the media items
      setHasInitialized(false);
      fetchMediaItems();
      setIsRenameDialogOpen(false);
      setCurrentRenameItem(null);
      setNewFilename("");
    } catch (error) {
      console.error("Error renaming file:", error);
      toast({
        title: "Rename Error",
        description: "Failed to rename file",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const filesToDelete = selectedItems
        .map((id) => mediaItems.find((item) => item.id === id)?.path)
        .filter(Boolean) as string[];

      const { error } = await supabase.storage
        .from("media")
        .remove(filesToDelete);

      if (error) throw error;

      toast({
        title: "Files Deleted",
        description: `${selectedItems.length} files have been removed from the library`,
        variant: "destructive",
      });

      // Refresh the media items
      setHasInitialized(false);
      fetchMediaItems();
      setSelectedItems([]);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting files:", error);
      toast({
        title: "Delete Error",
        description: "Failed to delete selected files",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render the preview dialog
  const renderPreviewDialog = () => {
    if (!previewItem) return null;

    const isImage = previewItem.type.startsWith("image/");
    const isAudio = previewItem.type.startsWith("audio/");

    return (
      <Dialog
        open={isPreviewDialogOpen}
        onOpenChange={(open) => {
          setIsPreviewDialogOpen(open);
          if (!open) setPreviewItem(null);
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle className="mr-8 truncate">
                {previewItem.name}
              </DialogTitle>
            </div>
            <DialogDescription>
              {formatFileSize(previewItem.size)} • Uploaded{" "}
              {formatDate(previewItem.uploadedAt)} • Owner: {previewItem.owner}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center items-center bg-muted/30 rounded-md overflow-hidden">
            {isImage ? (
              <img
                src={previewItem.url}
                alt={previewItem.name}
                className="max-h-[60vh] object-contain"
              />
            ) : isAudio ? (
              <div className="w-full p-4">
                <audio controls className="w-full">
                  <source src={previewItem.url} type={previewItem.type} />
                  Your browser does not support the audio element.
                </audio>
                <div className="flex justify-center mt-4">
                  <FileAudio className="h-16 w-16 text-blue-500" />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-8">
                <FileImage className="h-16 w-16 text-muted-foreground" />
                <p className="mt-4 text-sm text-muted-foreground">
                  Preview not available
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              File path: {previewItem.path}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(previewItem.url);
                  toast({
                    title: "Copied to clipboard",
                    description: "Media URL copied",
                  });
                }}
              >
                Copy URL
              </Button>
              <Button onClick={() => window.open(previewItem.url, "_blank")}>
                Download
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Render the upload dialog
  const renderUploadDialog = () => (
    <Dialog
      open={isUploadDialogOpen}
      onOpenChange={(open) => {
        if (!open) {
          // Reset upload state when dialog is closed
          setUploadProgress({ total: 0, completed: 0, isUploading: false });
        }
        setIsUploadDialogOpen(open);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload files</DialogTitle>
          <DialogDescription>
            Upload images and audio files to your media library
            {/* Note: Files will be uploaded to your personal folder */}
            <div className="mt-2 text-amber-500 dark:text-amber-400 text-sm">
              Note: Files will be uploaded to your personal folder (
              {user?.uid?.substring(0, 8)}...).
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {uploadProgress.isUploading ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-1">
                <div className="text-sm font-medium">Uploading files...</div>
                <div className="text-sm text-muted-foreground">
                  {uploadProgress.completed} of {uploadProgress.total} files
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2.5">
                <div
                  className="bg-primary h-2.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (uploadProgress.completed / uploadProgress.total) * 100
                    }%`,
                  }}
                ></div>
              </div>
              <div className="flex justify-center mt-4">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            </div>
          ) : (
            <>
              <div
                className="border-2 border-dashed rounded-lg p-12 text-center hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => document.getElementById("file-upload")?.click()}
              >
                <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="mt-2 text-sm font-medium">
                  Drag and drop files here or click to select
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Support: JPG, PNG (up to {formatFileSize(MAX_IMAGE_SIZE)}),
                  MP3, WAV, OGG, M4A, AAC (up to{" "}
                  {formatFileSize(MAX_AUDIO_SIZE)})
                </p>
                <Input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/x-m4a,audio/aac"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) {
                      // Validate each file before setting
                      for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const isAudioFile = file.type.startsWith("audio/");
                        const maxSize = isAudioFile
                          ? MAX_AUDIO_SIZE
                          : MAX_IMAGE_SIZE;

                        if (file.size > maxSize) {
                          toast({
                            title: "File too large",
                            description: `File "${
                              file.name
                            }" exceeds the maximum size limit of ${formatFileSize(
                              maxSize
                            )}`,
                            variant: "destructive",
                          });
                          e.target.value = ""; // Reset the input
                          return;
                        }

                        if (
                          isAudioFile &&
                          !SUPPORTED_AUDIO_TYPES.includes(file.type)
                        ) {
                          toast({
                            title: "Unsupported format",
                            description: `File "${file.name}" is not a supported audio format. Please use MP3, WAV, OGG, M4A, or AAC.`,
                            variant: "destructive",
                          });
                          e.target.value = ""; // Reset the input
                          return;
                        }
                      }
                      setUploadFiles(files);
                    }
                  }}
                />
              </div>
              {uploadFiles && (
                <div className="mt-4">
                  <p className="text-sm font-medium">
                    Selected files: {uploadFiles.length}
                  </p>
                  <div className="text-xs text-muted-foreground max-h-40 overflow-y-auto mt-2">
                    {Array.from(uploadFiles).map((file, index) => (
                      <div
                        key={index}
                        className="py-1 border-b border-border last:border-b-0"
                      >
                        <div className="flex items-center">
                          {file.type.startsWith("image/") ? (
                            <FileImage className="h-4 w-4 mr-2 text-blue-500" />
                          ) : file.type.startsWith("audio/") ? (
                            <FileAudio className="h-4 w-4 mr-2 text-green-500" />
                          ) : (
                            <FileImage className="h-4 w-4 mr-2 text-muted-foreground" />
                          )}
                          <span className="truncate">{file.name}</span>
                          <span className="ml-1 text-muted-foreground">
                            ({formatFileSize(file.size)})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsUploadDialogOpen(false)}
            disabled={uploadProgress.isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFileUpload}
            disabled={
              !uploadFiles ||
              uploadFiles.length === 0 ||
              uploadProgress.isUploading
            }
          >
            {uploadProgress.isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Render the rename dialog
  const renderRenameDialog = () => (
    <Dialog
      open={isRenameDialogOpen}
      onOpenChange={(open) => {
        setIsRenameDialogOpen(open);
        if (!open) {
          setCurrentRenameItem(null);
          setNewFilename("");
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename file</DialogTitle>
          <DialogDescription>
            Enter a new name for your file. The file extension will be
            preserved.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div>
            <p className="text-sm font-medium mb-2">Current name:</p>
            <p className="text-sm text-muted-foreground">
              {currentRenameItem?.name}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium mb-2">New name:</p>
            <Input
              placeholder="Enter new filename"
              value={newFilename}
              onChange={(e) => setNewFilename(e.target.value)}
              autoFocus
            />
          </div>
          <div className="text-xs text-amber-500 dark:text-amber-400">
            <p>
              ⚠️ Warning: If this file is already in use elsewhere in the
              application, renaming it may break those references.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsRenameDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleRenameFile} disabled={!newFilename.trim()}>
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Render the delete confirmation dialog
  const renderDeleteDialog = () => (
    <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete files</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {selectedItems.length} selected
            files? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-amber-500 dark:text-amber-400 mb-4">
          <p>
            ⚠️ Warning: If these files are in use elsewhere in the application,
            deleting them will break those references.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDeleteSelected}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (!user) {
    return null; // Or redirect to login
  }

  return (
    <AdminLayout
      title="Media Library"
      actionButton={
        <Button onClick={() => setIsUploadDialogOpen(true)}>
          <Upload className="mr-2 h-4 w-4" /> Upload Files
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Tabs
              defaultValue="all"
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-[400px]"
            >
              <TabsList>
                <TabsTrigger value="all">All Media</TabsTrigger>
                <TabsTrigger value="images">Images</TabsTrigger>
                <TabsTrigger value="audio">Audio</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center space-x-2">
            <div className="border rounded-md p-1 flex">
              <Button
                variant={viewStyle === "grid" ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewStyle("grid")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewStyle === "list" ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewStyle("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <select
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
              value={sortOrder}
              onChange={(e) =>
                setSortOrder(
                  e.target.value as
                    | "newest"
                    | "oldest"
                    | "name"
                    | "size-asc"
                    | "size-desc"
                )
              }
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">By Name</option>
              <option value="size-asc">Size: Smallest First</option>
              <option value="size-desc">Size: Largest First</option>
            </select>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search files..."
                className="w-[200px] pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
              value={uploadedByFilter}
              onChange={(e) =>
                setUploadedByFilter(
                  e.target.value as "all" | "users" | "admins"
                )
              }
            >
              <option value="all">Uploaded By: All</option>
              <option value="users">Uploaded By: Users</option>
              <option value="admins">Uploaded By: Admins</option>
            </select>
            <Button
              variant="outline"
              size="icon"
              onClick={refreshMediaItems}
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
            </Button>
            {filteredMediaItems.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                className="gap-2"
              >
                {selectedItems.length === filteredMediaItems.length ? (
                  <>
                    <CheckSquare className="h-4 w-4" /> Deselect All
                  </>
                ) : (
                  <>
                    <Square className="h-4 w-4" /> Select All
                  </>
                )}
              </Button>
            )}
            {selectedItems.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Selected ({selectedItems.length})
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => (
              <Card key={index} className="h-[220px] animate-pulse">
                <CardContent className="p-0">
                  <div className="h-full bg-muted/50"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredMediaItems.length > 0 ? (
          <>
            {viewStyle === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {paginatedItems.map((item) => (
                  <Card
                    key={item.id}
                    className={`overflow-hidden transition-all relative ${
                      selectedItems.includes(item.id)
                        ? "ring-2 ring-primary"
                        : "hover:shadow-md"
                    }`}
                    onClick={(e) => handleItemClick(item.id, e)}
                  >
                    <CardContent className="p-0">
                      <div className="relative h-[140px]">
                        {/* Selection checkbox shown in top-right corner */}
                        <div
                          className="absolute top-2 right-2 z-10 checkbox-area"
                          onClick={(e) => handleCheckboxToggle(item.id, e)}
                        >
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            className="h-5 w-5 bg-white/90 border-gray-300 rounded-sm"
                          />
                        </div>
                        {/* Preview button for quick view */}
                        <div
                          className="absolute top-2 left-2 z-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleItemClick(item.id);
                          }}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 bg-white/90 rounded-full hover:bg-white"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* New badge for recently uploaded files */}
                        {new Date().getTime() -
                          new Date(item.uploadedAt).getTime() <
                          3600000 && (
                          <div className="absolute top-2 left-12 z-10">
                            <Badge className="bg-green-500 hover:bg-green-600">
                              New
                            </Badge>
                          </div>
                        )}

                        {item.type.startsWith("image/") ? (
                          <div
                            className="absolute inset-0 bg-center bg-cover bg-no-repeat"
                            style={{
                              backgroundImage: `url(${item.url})`,
                            }}
                          ></div>
                        ) : item.type.startsWith("audio/") ? (
                          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
                            <FileAudio className="h-16 w-16 text-blue-500" />
                          </div>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center bg-muted">
                            <FileImage className="h-16 w-16 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="truncate font-medium text-sm">
                            {item.name}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              asChild
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="lucide lucide-more-horizontal"
                                >
                                  <circle cx="12" cy="12" r="1" />
                                  <circle cx="19" cy="12" r="1" />
                                  <circle cx="5" cy="12" r="1" />
                                </svg>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(item.url, "_blank");
                                }}
                              >
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(item.url);
                                  toast({
                                    title: "Copied to clipboard",
                                    description: "Media URL copied",
                                  });
                                }}
                              >
                                Copy Link
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentRenameItem(item);
                                  setNewFilename(item.name.split(".")[0]);
                                  setIsRenameDialogOpen(true);
                                }}
                              >
                                <Edit className="mr-2 h-4 w-4" /> Rename
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedItems([item.id]);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-xs text-muted-foreground">
                            {formatFileSize(item.size)}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30px]"></TableHead>
                      <TableHead className="w-[40%]">Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedItems.map((item) => (
                      <TableRow
                        key={item.id}
                        className={
                          selectedItems.includes(item.id) ? "bg-primary/5" : ""
                        }
                      >
                        <TableCell className="p-2">
                          <div
                            className="checkbox-area"
                            onClick={(e) => handleCheckboxToggle(item.id, e)}
                          >
                            <Checkbox
                              checked={selectedItems.includes(item.id)}
                              className="h-4 w-4 rounded-sm"
                            />
                          </div>
                        </TableCell>
                        <TableCell
                          className="font-medium cursor-pointer"
                          onClick={(e) => handleItemClick(item.id, e)}
                        >
                          <div className="flex items-center gap-2">
                            {item.type.startsWith("image/") ? (
                              <div
                                className="h-9 w-9 rounded-md bg-cover bg-center bg-no-repeat"
                                style={{
                                  backgroundImage: `url(${item.url})`,
                                }}
                              ></div>
                            ) : item.type.startsWith("audio/") ? (
                              <div className="h-9 w-9 rounded-md bg-blue-50 dark:bg-blue-950 flex items-center justify-center">
                                <FileAudio className="h-5 w-5 text-blue-500" />
                              </div>
                            ) : (
                              <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center">
                                <FileImage className="h-5 w-5 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="truncate max-w-[200px]">
                                  {item.name}
                                </span>
                                {new Date().getTime() -
                                  new Date(item.uploadedAt).getTime() <
                                  3600000 && (
                                  <Badge className="bg-green-500 hover:bg-green-600">
                                    New
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {item.type.split("/")[1] || item.type.split("/")[0]}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatFileSize(item.size)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {formatDate(item.uploadedAt)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end space-x-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(item.id);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentRenameItem(item);
                                setNewFilename(item.name.split(".")[0]);
                                setIsRenameDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-more-horizontal"
                                  >
                                    <circle cx="12" cy="12" r="1" />
                                    <circle cx="19" cy="12" r="1" />
                                    <circle cx="5" cy="12" r="1" />
                                  </svg>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    window.open(item.url, "_blank");
                                  }}
                                >
                                  Download
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    navigator.clipboard.writeText(item.url);
                                    toast({
                                      title: "Copied to clipboard",
                                      description: "Media URL copied",
                                    });
                                  }}
                                >
                                  Copy Link
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={(e) => {
                                    setSelectedItems([item.id]);
                                    setIsDeleteDialogOpen(true);
                                  }}
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination Controls */}
            <div className="flex items-center justify-between px-4 py-4 border-t border-border mt-4">
              <div className="text-sm text-muted-foreground">
                Showing{" "}
                <span className="font-medium">
                  {Math.min(
                    (currentPage - 1) * pageSize + 1,
                    filteredMediaItems.length
                  )}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, filteredMediaItems.length)}
                </span>{" "}
                of{" "}
                <span className="font-medium">{filteredMediaItems.length}</span>{" "}
                files
                {filteredMediaItems.length !== totalItems && (
                  <span> (filtered from {totalItems} total)</span>
                )}
              </div>
              <div className="flex items-center gap-4">
                {totalPages > 0 && (
                  <div className="flex items-center">
                    <Button
                      className="mr-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={!canGoPrev}
                    >
                      First
                    </Button>

                    <Button
                      className="mr-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                      }
                      disabled={!canGoPrev}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    {pageNumbers.map((pageNum) => (
                      <Button
                        key={pageNum}
                        className="mx-0.5 w-8 h-8 p-0"
                        variant={
                          pageNum === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    ))}

                    <Button
                      className="ml-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(
                          currentPage < totalPages
                            ? currentPage + 1
                            : totalPages
                        )
                      }
                      disabled={!canGoNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>

                    <Button
                      className="ml-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={!canGoNext}
                    >
                      Last
                    </Button>
                  </div>
                )}

                <select
                  className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  {[15, 30, 45, 60].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-muted p-3">
              <FileImage className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-lg font-medium">No files found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {searchQuery
                ? "Try adjusting your search query"
                : mediaItems.length > 0
                ? `All ${mediaItems.length} files are filtered out by the current settings`
                : "No files found in any folder"}
            </p>
            {process.env.NODE_ENV === "development" && (
              <div className="mt-4 p-4 bg-muted/30 rounded-md max-w-xl">
                <p className="text-xs text-muted-foreground mb-2">
                  Debug information:
                </p>
                <ul className="text-xs text-muted-foreground list-disc list-inside">
                  <li>Total files in storage: {mediaItems.length}</li>
                  <li>Current tab filter: {activeTab}</li>
                  <li>Search query: {searchQuery || "(none)"}</li>
                  <li>Available folders: {folders.join(", ") || "none"}</li>
                </ul>
              </div>
            )}
            <Button
              className="mt-4"
              onClick={() => setIsUploadDialogOpen(true)}
            >
              <Upload className="mr-2 h-4 w-4" /> Upload Files
            </Button>
          </div>
        )}
      </div>

      {renderUploadDialog()}
      {renderDeleteDialog()}
      {renderRenameDialog()}
      {renderPreviewDialog()}
    </AdminLayout>
  );
};

export default MediaLibrary;
